import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { defineMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { SystemMessage } from '@atlassian/conversation-assistant-preview-card/system-message';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { isStoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { analyticsAttributes } from './constants';
import { TaskCard } from './task-card';

const successMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jsm-solution-composer-plan.success',
	defaultMessage: 'You created {title} space',
	description:
		'Success pill message shown after a JSM space has been created from the solution composer plan.',
});

export const JsmSolutionComposerPlanAction = ({
	actions,
	sendMessageConfirmedTools,
	conversationId,
	messageId,
	recipientAgentId,
	isHistoricalMessage,
}: ActionRendererFor<
	typeof ActionKeys.JsmSolutionComposerPlanSummary
>): React.JSX.Element | null => {
	di(TaskCard);

	const { formatMessage } = useIntl();
	const { trackAIMAUEvent, sendAnalyticsEvent } = useAnalytics();
	const { getAIFeatureContext, setAIFeatureContext } = useChatContextStoreActions();

	const [conversationMessages] = useChatMessages({ conversationId: conversationId ?? '' });

	const isSuperseded = useMemo(() => {
		if (!messageId) {
			return false;
		}
		const myIndex = conversationMessages.findIndex(
			(m) => isStoreAssistant(m) && m.content.message.message.id === messageId,
		);
		if (myIndex === -1) {
			return false;
		}
		return conversationMessages
			.slice(myIndex + 1)
			.some(
				(m) =>
					isStoreAssistant(m) &&
					m.content.message.message.actions?.some(
						(a) => a.key === ActionKeys.JsmSolutionComposerPlanSummary,
					),
			);
	}, [conversationMessages, messageId]);

	const firstAction = actions[0];

	const handleCreateSpace = useCallback(async () => {
		if (!firstAction || !sendMessageConfirmedTools || !conversationId || !messageId) {
			return;
		}

		const previousSolutionComposerContext = getAIFeatureContext()?.solutionComposerContext;
		setAIFeatureContext({
			contextKey: 'solutionComposerContext',
			setContext: <T,>(_: T): T => ({ domainType: 'SERVICE', skillTypes: null }) as T,
		});

		try {
			await sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'jsmCreateSpaceAction',
				},
				includeAIFeatureContext: true,
				confirmed_tools: {
					message_id: messageId,
					tools: [
						{
							key: firstAction.key,
							tool_id: firstAction.actionId,
							tool_source: firstAction.toolSource,
							invocation_id: firstAction.invocationId,
							data: {
								human_message_id: firstAction.humanMessageId ?? '',
								arguments: {
									type: 'confirm',
								},
							},
						},
					],
				},
			});
		} finally {
			setAIFeatureContext({
				contextKey: 'solutionComposerContext',
				setContext: <T,>(_: T): T => previousSolutionComposerContext as T,
			});
		}
	}, [
		firstAction,
		sendMessageConfirmedTools,
		conversationId,
		messageId,
		recipientAgentId,
		getAIFeatureContext,
		setAIFeatureContext,
	]);

	const isFeatureGateEnabled = fg('solution_composer_master');
	const isSpaceKeyValidationEnabled = fg('rovo_chat_jsm_composer_s2_space_key_validation');
	const isExecuted = firstAction?.data?.executed;
	const validatedProjectKey = isSpaceKeyValidationEnabled
		? firstAction?.data?.projectKey
		: undefined;
	const projectKey = isExecuted ? firstAction?.data?.key : validatedProjectKey;
	const hasCreatedSpace = Boolean(isExecuted && projectKey);

	const hasFiredViewedRef = useRef(false);
	const hasFiredAdoptedRef = useRef(false);
	useEffect(() => {
		if (!firstAction || !isFeatureGateEnabled || isHistoricalMessage) {
			return;
		}

		if (!hasCreatedSpace && !hasFiredViewedRef.current) {
			hasFiredViewedRef.current = true;
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'jsmSolutionComposerPlanCard',
				actionSubjectId: 'jsmSolutionComposerPlanCard',
				attributes: {
					...analyticsAttributes,
					conversationId,
					messageId,
				},
			});
		}

		if (hasCreatedSpace && !hasFiredAdoptedRef.current) {
			hasFiredAdoptedRef.current = true;
			// added adopted event here as this is tracked after sendMessageConfirmedTools
			trackAIMAUEvent({
				type: 'adopted',
				actionKey: firstAction.key,
				proactive: true,
				rovoModifiedObjects: [{ objectType: 'jsm-project', objectId: projectKey as string }],
				messageId,
				attributes: analyticsAttributes,
			});

			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'jsmSolutionComposerSpaceCard',
				actionSubjectId: 'jsmSolutionComposerSpaceCard',
				attributes: {
					...analyticsAttributes,
					conversationId,
					messageId,
				},
			});
		}
	}, [
		firstAction,
		isFeatureGateEnabled,
		isHistoricalMessage,
		hasCreatedSpace,
		projectKey,
		messageId,
		conversationId,
		trackAIMAUEvent,
		sendAnalyticsEvent,
	]);

	if (!firstAction || !isFeatureGateEnabled) {
		return null;
	}

	const title: string = firstAction.data.name ?? '';
	const projectName: string | undefined = firstAction.data.projectName;

	return (
		<>
			<TaskCard
				title={title}
				sections={firstAction.data.sections ?? []}
				onCreateSpace={handleCreateSpace}
				isExecuted={isExecuted}
				projectKey={projectKey}
				projectName={projectName}
				isDisabled={isSuperseded}
			/>
			{hasCreatedSpace ? (
				<SystemMessage message={formatMessage(successMessage, { title })} />
			) : null}
		</>
	);
};
